// 手数料自動考慮 (スポット AC-BSP-004b / AC-BSP-106、 時間前 AC-BIN-004b / AC-BIN-104) の、 backend の
// プレビュー RPC の応答から「作成・保存してよいか・何を表示するか」 への写像。 入札価格の算出と値域・
// 入札制限の判定は backend が持つ (#8109)。
//
// proto に依存しない: スポットと時間前の RPC は同じ意味の鏡像 (#11470) なので、 各系統が自分の proto 型を
// 下の正規化型 (`FeeAutoConsiderState` / `FeeAdjustedPriceResult`) に写してから呼ぶ。
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import type { TFunction } from 'i18next'

/** RPC の Decimal 文字列 ("10.1" 等) を小数 2 桁で表示する。 */
export const formatYenPerKwh = (value: string): string =>
  Number(value).toFixed(2)

/** 算出した入札価格が外れた境界 (文言の SoT は `spot-bidding.md` AC-BSP-106)。 */
export type FeeAdjustmentViolation = {
  bound: 'min' | 'max'
  source: 'jepx' | 'bidLimit'
  side: Side.BUY | Side.SELL
  feeYenPerKwh: string
  orderPriceYenPerKwh: string
  boundOrderPriceYenPerKwh: string
  desiredPriceBoundYenPerKwh: string
}

const FEE_ADJUSTMENT_ERROR = 'page:biddingCommon.feeAdjustmentError'

/**
 * AC-BSP-106 の 1 行目と 2 行目。 スポット・時間前で同じ文と書式を使う (AC-BIN-104 は文言の SoT を AC-BSP-106 とする)。
 * {JEPX下限} / {JEPX上限} は両系統の design どおり小数 2 桁 (spec の ※ は 999 と書く。PdM 確認候補)。
 */
export const feeAdjustmentViolationLines = (
  t: TFunction,
  v: FeeAdjustmentViolation,
): { line1: string; line2: string } => {
  const desired = formatYenPerKwh(v.desiredPriceBoundYenPerKwh)
  const line1 = t(
    `${FEE_ADJUSTMENT_ERROR}.${v.bound === 'min' ? 'errorDesiredMin' : 'errorDesiredMax'}`,
    { desired },
  )
  const reason = t(
    `${FEE_ADJUSTMENT_ERROR}.${v.side === Side.SELL ? 'errorReasonAdd' : 'errorReasonSubtract'}`,
    { fee: formatYenPerKwh(v.feeYenPerKwh) },
  )
  const detail =
    v.source === 'jepx'
      ? t(`${FEE_ADJUSTMENT_ERROR}.errorJepxBound`, {
          desired,
          bound: formatYenPerKwh(v.boundOrderPriceYenPerKwh),
        })
      : t(
          `${FEE_ADJUSTMENT_ERROR}.${v.bound === 'min' ? 'errorBidLimitMin' : 'errorBidLimitMax'}`,
          {
            price: formatYenPerKwh(v.orderPriceYenPerKwh),
            limit: formatYenPerKwh(v.boundOrderPriceYenPerKwh),
          },
        )
  // 2 行目は AC-BSP-106 の表どおり理由の文に続きをつなぐ (区切りは言語ごとに辞書が持つ)。
  const line2 = t(`${FEE_ADJUSTMENT_ERROR}.errorSecondLine`, { reason, detail })
  return { line1, line2 }
}

/** プレビュー RPC が返す組織の手数料自動考慮の状態 (系統の proto から写した形)。 */
export type FeeAutoConsiderState = {
  enabled: boolean
  feeYenPerKwh: string | undefined
  /** ON だが手数料単価を使えない。 */
  unavailable: boolean
}

/** プレビュー RPC が返す 1 行の算出結果 (系統の proto から写した形)。 未知の enum 値は undefined。 */
export type FeeAdjustedPriceResult = {
  status: 'off' | 'adjusted' | 'outOfRange' | 'indeterminate' | undefined
  orderPriceYenPerKwh: string | undefined
  violation:
    | {
        bound: FeeAdjustmentViolation['bound'] | undefined
        source: FeeAdjustmentViolation['source'] | undefined
        boundOrderPriceYenPerKwh: string | undefined
        desiredPriceBoundYenPerKwh: string | undefined
      }
    | undefined
}

const tradeSide = (side: Side): FeeAdjustmentViolation['side'] | undefined =>
  side === Side.BUY || side === Side.SELL ? side : undefined

/**
 * プレビュー RPC の取得状況。 `'idle'` は入力が揃わず問い合わせていない間、 `'loading'` は
 * 入力が最新の結果に追いついていない間 (入力待ち・取得中・取り直し中) を含む。
 */
export type FeeAdjustedPricePreviewQueryStatus =
  | 'idle'
  | 'loading'
  | 'error'
  | 'success'

/**
 * 問い合わせた後の取得状況。 失敗した後の再試行中は、 再試行が終わるのを待たずに取得失敗とする
 * (作成・編集の期限までに操作できなくなるのを避ける)。 `waiting` は使う結果を待っている間。
 */
export const previewQueryStatus = ({
  waiting,
  isError,
  failureCount,
  hasData,
}: {
  waiting: boolean
  isError: boolean
  failureCount: number
  hasData: boolean
}): 'loading' | 'error' | 'success' =>
  isError || failureCount > 0
    ? 'error'
    : waiting
      ? 'loading'
      : hasData
        ? 'success'
        : 'error'

/** 入札制限の設定 (`GetBidLimitMatrix`) の手数料自動考慮の ON/OFF。 */
export type FeeAutoConsiderMatrixSetting =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'success'; enabled: boolean }

export const toFeeAutoConsiderMatrixSetting = ({
  isLoading,
  isError,
  enabled,
}: {
  isLoading: boolean
  isError: boolean
  enabled: boolean
}): FeeAutoConsiderMatrixSetting =>
  isLoading
    ? { status: 'loading' }
    : isError
      ? { status: 'error' }
      : { status: 'success', enabled }

/**
 * 欄名・検証を作り、 プレビューの応答と突き合わせる手数料自動考慮の扱い。 `fallbackOff` は手数料単価を
 * 取得できず OFF として扱う間で、 入力値をそのまま入札価格にする。 `setting` はその間の案内を出し分ける
 * 入札制限の設定。
 */
export type FeeAutoConsiderTreatment =
  | FeeAutoConsiderMatrixSetting
  | { status: 'fallbackOff'; setting: FeeAutoConsiderMatrixSetting }

/** 価格欄が希望価格か: 手数料自動考慮が ON の扱いで、 OFF として扱っていない間だけ。 */
export const usesDesiredPrice = (
  treatment: FeeAutoConsiderTreatment,
  enabled: boolean,
): boolean => treatment.status !== 'fallbackOff' && enabled

/** 作成・下書きの編集 (Update) の RPC に付ける、 プレビューで見ていた手数料自動考慮の状態 (希望価格は入力値)。 */
export type FeeAutoConsiderConfirmed = {
  enabled: boolean
  /** ON のときだけ持つ。 */
  feeYenPerKwh?: string
}

export type FeeAutoConsiderBlockedReason =
  | 'loading'
  /** 手数料単価を使えない (取得失敗・再試行中・単価を使えない応答)。 OFF に切り替える引き金。 */
  | 'unavailable'
  /** 応答が欄名・検証を作った扱いと食い違う (ON/OFF・値の欠け・OFF として計算させたのに OFF でない結果)。 */
  | 'settingMismatch'

export type FeeAutoConsiderPreview =
  | { kind: 'idle'; hint: 'incomplete' | 'off' | 'loading' | 'settingUnknown' }
  | {
      kind: 'ready'
      confirmed: FeeAutoConsiderConfirmed
      /** ON で算出できた時だけ持つ (表示用に整形済み)。 */
      orderPriceYenPerKwh?: string
    }
  | {
      kind: 'outOfRange'
      orderPriceYenPerKwh: string
      violation: FeeAdjustmentViolation
    }
  | { kind: 'blocked'; reason: FeeAutoConsiderBlockedReason }
  /**
   * 手数料単価を取得できず OFF として扱う間。 作成・保存を止めない。 入札制限の設定が OFF と分かっている
   * 組織には、 取得できない旨でなく OFF の案内を出す (`settingOff`)。
   */
  | { kind: 'fallbackOff'; settingOff: boolean }

const blocked = (
  reason: FeeAutoConsiderBlockedReason,
): FeeAutoConsiderPreview => ({
  kind: 'blocked',
  reason,
})

/** OFF として扱う間の判定 (入札制限の設定が OFF と分かっている時は OFF の案内)。 */
export const fallbackOffPreview = (
  setting: FeeAutoConsiderMatrixSetting,
): FeeAutoConsiderPreview => ({
  kind: 'fallbackOff',
  settingOff: setting.status === 'success' && !setting.enabled,
})

/**
 * プレビュー RPC の結果と欄名・検証を作った扱い (`FeeAutoConsiderTreatment`) から、 作成・保存して
 * よいかを 1 箇所で決める。
 *
 * - プレビューが取れた: その状態が正。 扱いの ON/OFF と食い違う時・値が欠けた時は、 欄名・検証が
 *   別の ON/OFF で作られているため止めて再読み込みを促す。 入札制限の取得失敗は OFF 前提の画面
 *   なので、 プレビューが ON の時だけ止める
 * - プレビュー取得失敗: 入札制限の設定で OFF と確定していれば止めず OFF の明示入力を送る (#8052 の
 *   判断。 実は ON なら backend が `FEE_AUTO_CONSIDER_CHANGED` で拒否する)。 それ以外は手数料単価を
 *   使えない
 */
export const resolveFeeAutoConsiderPreview = ({
  status,
  state,
  result,
  side,
  treatment,
}: {
  status: FeeAdjustedPricePreviewQueryStatus
  state: FeeAutoConsiderState | undefined
  result: FeeAdjustedPriceResult | undefined
  side: Side
  treatment: FeeAutoConsiderTreatment
}): FeeAutoConsiderPreview => {
  if (treatment.status === 'fallbackOff') {
    return fallbackOffPreview(treatment.setting)
  }
  if (status === 'idle') {
    if (treatment.status === 'loading') {
      return { kind: 'idle', hint: 'loading' }
    }
    if (treatment.status === 'error') {
      return { kind: 'idle', hint: 'settingUnknown' }
    }
    return { kind: 'idle', hint: treatment.enabled ? 'incomplete' : 'off' }
  }
  if (status === 'loading' || treatment.status === 'loading') {
    return blocked('loading')
  }
  if (status === 'error') {
    return treatment.status === 'success' && !treatment.enabled
      ? { kind: 'ready', confirmed: { enabled: false } }
      : blocked('unavailable')
  }
  if (state === undefined || result === undefined) {
    return blocked('settingMismatch')
  }
  // 入札制限を取得できない間、 欄名・検証は OFF 前提で作られている (#8052 の「入札制限の
  // 取得失敗は作成を止めない」)。 プレビューも OFF なら画面と一致するので止めない。
  const treatedEnabled = treatment.status === 'success' && treatment.enabled
  if (treatedEnabled !== state.enabled) {
    return blocked('settingMismatch')
  }
  if (!state.enabled) {
    return result.status === 'off'
      ? { kind: 'ready', confirmed: { enabled: false } }
      : blocked('settingMismatch')
  }
  const fee = state.feeYenPerKwh
  if (
    state.unavailable ||
    fee === undefined ||
    result.status === 'indeterminate'
  ) {
    return blocked('unavailable')
  }
  const orderPrice = result.orderPriceYenPerKwh
  if (result.status === 'adjusted' && orderPrice !== undefined) {
    return {
      kind: 'ready',
      confirmed: { enabled: true, feeYenPerKwh: fee },
      orderPriceYenPerKwh: formatYenPerKwh(orderPrice),
    }
  }
  if (result.status === 'outOfRange') {
    const v = result.violation
    const s = tradeSide(side)
    if (
      orderPrice !== undefined &&
      v?.bound !== undefined &&
      v.source !== undefined &&
      s !== undefined &&
      v.boundOrderPriceYenPerKwh !== undefined &&
      v.desiredPriceBoundYenPerKwh !== undefined
    ) {
      return {
        kind: 'outOfRange',
        orderPriceYenPerKwh: formatYenPerKwh(orderPrice),
        violation: {
          bound: v.bound,
          source: v.source,
          side: s,
          feeYenPerKwh: fee,
          orderPriceYenPerKwh: orderPrice,
          boundOrderPriceYenPerKwh: v.boundOrderPriceYenPerKwh,
          desiredPriceBoundYenPerKwh: v.desiredPriceBoundYenPerKwh,
        },
      }
    }
  }
  return blocked('settingMismatch')
}

/**
 * OFF として扱う間の、 計算結果を入力にするプレビューの判定。 backend に OFF として計算させるので、
 * 計算できた応答の入札価格が OFF (計算結果のまま) なら止めない。 OFF でない結果は、 欄名と別の価格を
 * 保存しないよう止める。 計算できない・取得できない間は止めない (保存時に backend が判定する)。
 */
export const resolveFeeFallbackOffPreview = ({
  status,
  result,
  setting,
}: {
  status: FeeAdjustedPricePreviewQueryStatus
  result: FeeAdjustedPriceResult | undefined
  setting: FeeAutoConsiderMatrixSetting
}): FeeAutoConsiderPreview =>
  status === 'success' && result?.status !== 'off'
    ? blocked('settingMismatch')
    : fallbackOffPreview(setting)

export type FeeAutoConsiderHint = {
  /** 補足表示の種類。 系統ごとに自分の i18n key へ写す (時間前は key の末尾にそのまま使う)。 */
  key:
    | 'feeAutoConsiderLoading'
    | 'feeAutoConsiderUnavailable'
    | 'feeAutoConsiderSettingMismatch'
    | 'feeAutoConsiderSettingUnknown'
    | 'feeAutoConsiderAdjusted'
    | 'feeAutoConsiderIncomplete'
    | 'feeAutoConsiderOff'
    | 'feeAutoConsiderFallbackOff'
  color: 'Text.highEmphasis' | 'Text.midEmphasis' | 'Text.negativeMid'
  orderPriceYenPerKwh?: string
}

const LOADING_HINT: FeeAutoConsiderHint = {
  key: 'feeAutoConsiderLoading',
  color: 'Text.midEmphasis',
}

const OFF_HINT: FeeAutoConsiderHint = {
  key: 'feeAutoConsiderOff',
  color: 'Text.midEmphasis',
}

const IDLE_HINTS: Record<
  Extract<FeeAutoConsiderPreview, { kind: 'idle' }>['hint'],
  FeeAutoConsiderHint
> = {
  incomplete: { key: 'feeAutoConsiderIncomplete', color: 'Text.midEmphasis' },
  off: OFF_HINT,
  loading: LOADING_HINT,
  settingUnknown: {
    key: 'feeAutoConsiderSettingUnknown',
    color: 'Text.negativeMid',
  },
}

const BLOCKED_HINTS: Record<FeeAutoConsiderBlockedReason, FeeAutoConsiderHint> =
  {
    loading: LOADING_HINT,
    unavailable: {
      key: 'feeAutoConsiderUnavailable',
      color: 'Text.negativeMid',
    },
    settingMismatch: {
      key: 'feeAutoConsiderSettingMismatch',
      color: 'Text.negativeMid',
    },
  }

/** 価格欄の下の補足表示。 */
export const feeAutoConsiderHint = (
  preview: FeeAutoConsiderPreview,
): FeeAutoConsiderHint => {
  switch (preview.kind) {
    case 'idle':
      return IDLE_HINTS[preview.hint]
    case 'blocked':
      return BLOCKED_HINTS[preview.reason]
    case 'ready':
      return preview.confirmed.enabled
        ? {
            key: 'feeAutoConsiderAdjusted',
            color: 'Text.highEmphasis',
            orderPriceYenPerKwh: preview.orderPriceYenPerKwh,
          }
        : OFF_HINT
    case 'outOfRange':
      return {
        key: 'feeAutoConsiderAdjusted',
        color: 'Text.negativeMid',
        orderPriceYenPerKwh: preview.orderPriceYenPerKwh,
      }
    case 'fallbackOff':
      return preview.settingOff
        ? OFF_HINT
        : { key: 'feeAutoConsiderFallbackOff', color: 'Text.midEmphasis' }
  }
}

/** プレビューが手数料単価を使えないことを示したか (開いた後に OFF へ切り替える引き金)。 */
export const revealsFeeUnavailable = (
  preview: FeeAutoConsiderPreview,
): boolean => preview.kind === 'blocked' && preview.reason === 'unavailable'
